import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, Trash2, BookOpen, Bookmark, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Page, BrandHeader } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { SoundPicker } from "@/components/SoundPicker";
import { InstallCard } from "@/components/InstallApp";
import { FeedbackForm } from "@/components/PilotFeedback";
import { Switch } from "@/components/ui/switch";
import { SPEEDS, DEFAULT_READ, readAloud, stopReading } from "@/lib/readAloud";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { usePrefs, useSavePrefs } from "@/lib/store";
import { defaultCrisisLine, DEFAULT_CALM_MESSAGE } from "@/lib/support";

const Section = ({ title, sub, children }) => (
  <section className="tf-card p-5 sm:p-6 mt-5">
    <h2 className="text-lg font-bold text-t-on">{title}</h2>
    {sub ? <p className="text-sm text-t-muted mt-1 mb-4">{sub}</p> : <div className="mb-4" />}
    {children}
  </section>
);
const Seg = ({ options, value, onChange, prefix }) => (
  <div className="inline-flex rounded-full bg-t-surface3 p-1 flex-wrap">
    {options.map(([k, label, swatch]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`px-4 h-9 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${value === k ? "bg-t-surface text-t-on shadow-sm" : "text-t-muted"}`}>
        {swatch ? <span className="w-3.5 h-3.5 rounded-full" style={{ background: swatch }} /> : null}{label}
      </button>
    ))}
  </div>
);

function useDraft(source, keys) {
  const [d, setD] = useState(() => Object.fromEntries(keys.map((k) => [k, source?.[k] ?? ""])));
  useEffect(() => {
    setD(Object.fromEntries(keys.map((k) => [k, source?.[k] ?? ""])));
  }, [source]); // eslint-disable-line
  return [d, (k) => (e) => setD({ ...d, [k]: e.target.value })];
}

function ReadAloudSettings({ prefs, save }) {
  const read = { ...DEFAULT_READ, ...(prefs?.readAloud || {}) };
  const update = (p) => save.mutate({ readAloud: { ...read, ...p } });
  const [previewing, setPreviewing] = useState(null);
  const preview = (id) => {
    if (previewing === id) return stopReading();
    readAloud("Take a slow breath. You're doing something kind for yourself, right now.", (st) => setPreviewing(st === "idle" || st === "error" ? null : id), id);
  };
  return (
    <div className="flex flex-col gap-5">
      <label className="flex items-center gap-3">
        <span className="flex-1">
          <span className="block font-semibold text-t-on">Read notes automatically</span>
          <span className="block text-[13px] text-t-muted">Each AI note starts playing as soon as it appears. Notes wait their turn instead of talking over each other.</span>
        </span>
        <Switch checked={read.autoPlay} onCheckedChange={(v) => update({ autoPlay: v })} data-testid="read-autoplay-switch" />
      </label>
      <div>
        <p className="text-sm font-semibold text-t-on2 mb-2">Reading speed</p>
        <div className="flex flex-col gap-2">
          {SPEEDS.map((s) => {
            const on = read.speed === s.id;
            return (
              <div key={s.id} className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${on ? "border-t-brand bg-t-brand3/60" : "border-t-border"}`}>
                <button type="button" onClick={() => update({ speed: s.id })} data-testid={`read-speed-${s.id}`} className="flex-1 flex items-center gap-3 text-left">
                  <span className={`w-6 h-6 rounded-full border-2 grid place-items-center ${on ? "bg-t-brand border-t-brand text-t-onbrand" : "border-t-border2"}`}>{on ? <span className="w-2 h-2 rounded-full bg-current" /> : null}</span>
                  <span><span className="block font-semibold text-t-on">{s.label}</span><span className="block text-[13px] text-t-muted">{s.desc}</span></span>
                </button>
                <button type="button" onClick={() => preview(s.id)} data-testid={`read-speed-preview-${s.id}`}
                  className="rounded-full bg-t-brand2 text-t-brand px-3.5 h-9 text-sm font-semibold">{previewing === s.id ? "Stop" : "Preview"}</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser, signOut } = useAuth();
  const { scheme, setScheme, accent, setAccent } = useTheme();
  const { data: prefs } = usePrefs();
  const save = useSavePrefs();
  const [name, setName] = useState(user?.name ?? "");
  const [contact, setContact] = useDraft(prefs?.safetyContact, ["name", "phone", "message"]);
  const def = defaultCrisisLine();
  const [line, setLine] = useDraft(prefs?.crisisLine, ["name", "number"]);
  const [therapist, setTherapist] = useDraft(prefs, ["therapistEmail", "therapistName"]);

  const saveName = async () => {
    const { data } = await api.patch("/auth/profile", { name });
    setUser(data);
    toast.success("Name updated");
  };
  const persist = (patch, msg) => save.mutateAsync(patch).then(() => toast.success(msg));
  const deleteAccount = async () => {
    await api.delete("/auth/account");
    toast.success("Your account has been deleted");
    signOut();
  };

  return (
    <Page>
      <div className="md:hidden"><BrandHeader /></div>
      <h1 className="mt-6 md:mt-4 text-4xl sm:text-5xl font-bold text-t-on">Settings</h1>

      <Section title="Your profile" sub={user?.email}>
        <div className="flex gap-3 items-center">
          {user?.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" /> : <span className="w-12 h-12 rounded-full bg-t-brand3 text-t-brand grid place-items-center font-bold text-lg">{(user?.name || "?").charAt(0).toUpperCase()}</span>}
          <input className="tf-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (shown on reports)" data-testid="profile-name-input" />
          <Btn size="md" label="Save" onClick={saveName} testId="profile-save-name" />
        </div>
      </Section>

      <Section title="Install on your phone" sub="Add Triggerific-Pilot to your home screen so it opens full-screen, with its own icon — like a regular app.">
        <InstallCard />
      </Section>

      <Section title="Appearance" sub="Choose a light or dark space, or follow your device.">
        <div className="flex flex-col gap-4">
          <Seg prefix="scheme" value={scheme} onChange={(v) => { setScheme(v); save.mutate({ scheme: v }); }} options={[["system", "System"], ["light", "Light"], ["dark", "Dark"]]} />
          <p className="text-sm font-semibold text-t-on2">Accent colour</p>
          <Seg prefix="accent" value={accent} onChange={(v) => { setAccent(v); save.mutate({ accent: v }); }} options={[["blue", "Blue", "#5E8FD6"], ["lavender", "Lavender", "#8E86D6"], ["teal", "Teal", "#3FA8A0"]]} />
        </div>
      </Section>

      <Section title="Sounds & voice" sub="Choose your go-to calming tone for the Get Help Now override. Tap ▶ to preview. One soothing guide voice walks you through every breath.">
        <SoundPicker />
      </Section>

      <Section title="Reading AI notes aloud" sub="The same calm, steady voice can read your validations, reflections, Flip Coach replies and need ideas.">
        <ReadAloudSettings prefs={prefs} save={save} />
      </Section>

      <Section title="Safety contact" sub="A trusted person we offer to call or text at the end of a hard check-in, once you feel steadier.">
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="tf-input" placeholder="Their name" value={contact.name} onChange={setContact("name")} data-testid="contact-name-input" />
          <input className="tf-input" placeholder="Phone number" value={contact.phone} onChange={setContact("phone")} data-testid="contact-phone-input" />
          <textarea className="tf-input sm:col-span-2" placeholder={DEFAULT_CALM_MESSAGE} value={contact.message} onChange={setContact("message")} data-testid="contact-message-input" />
        </div>
        <Btn size="md" className="mt-3" label="Save contact" onClick={() => persist({ safetyContact: contact }, "Safety contact saved")} testId="contact-save" />
      </Section>

      <Section title="Crisis line" sub={`The helpline offered at the end of a hard check-in. Default for your region: ${def.name} (${def.display}).`}>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="tf-input" placeholder={def.name} value={line.name} onChange={setLine("name")} data-testid="crisis-name-input" />
          <input className="tf-input" placeholder={def.number} value={line.number} onChange={setLine("number")} data-testid="crisis-number-input" />
        </div>
        <Btn size="md" className="mt-3" label="Save crisis line" onClick={() => persist({ crisisLine: line.number ? { ...line, display: line.number } : null }, "Crisis line saved")} testId="crisis-save" />
      </Section>

      <Section title="Morning check" sub="A gentle one-tap mood prompt in Affirmations between 5am and 11am. It counts as a mood log in your History.">
        <label className="flex items-center gap-3">
          <span className="flex-1 font-semibold text-t-on">Show the morning check</span>
          <Switch checked={prefs?.morningCheck !== false} disabled={!prefs} onCheckedChange={(v) => save.mutate({ morningCheck: v })} data-testid="morning-check-switch" />
        </label>
      </Section>

      <Section title="Psychologist reports" sub="Save your psychologist's email once, then send a PDF report from History in one tap. They get a secure link that expires after 14 days.">
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="tf-input" placeholder="Psychologist name" value={therapist.therapistName} onChange={setTherapist("therapistName")} data-testid="therapist-name-input" />
          <input className="tf-input" type="email" placeholder="psychologist@clinic.com" value={therapist.therapistEmail} onChange={setTherapist("therapistEmail")} data-testid="therapist-email-input" />
        </div>
        <Btn size="md" className="mt-3" label="Save" onClick={() => persist(therapist, "Psychologist details saved")} testId="therapist-save" />
        <label className="mt-5 flex items-center gap-3">
          <span className="flex-1">
            <span className="block font-semibold text-t-on">Email me when my psychologist leaves a note</span>
            <span className="block text-[13px] text-t-muted">A short heads-up to {user?.email}. The note itself stays private in the app.</span>
          </span>
          <Switch checked={prefs?.noteAlerts !== false} disabled={!prefs} onCheckedChange={(v) => save.mutate({ noteAlerts: v })} data-testid="note-alerts-switch" />
        </label>
      </Section>

      <Section title="Share feedback" sub="You're one of our pilot testers. Tell us what's helping, what's confusing, or what's broken — every message is read.">
        <FeedbackForm />
      </Section>

      <Section title="Library">
        {[["/schemas", "Schema library", BookOpen, "settings-schemas"], ["/cards", "My script cards", Bookmark, "settings-cards"], ...(user?.is_admin ? [["/admin", "Admin analytics", BarChart3, "settings-admin"]] : [])].map(([to, label, Icon, tid]) => (
          <button key={to} onClick={() => navigate(to)} data-testid={tid} className="w-full flex items-center gap-3 py-3 border-b last:border-0 border-t-border text-t-on font-medium">
            <Icon size={18} className="text-t-brand" /> <span className="flex-1 text-left">{label}</span> <ChevronRight size={18} className="text-t-muted" />
          </button>
        ))}
      </Section>

      <Section title="Account">
        <div className="flex flex-wrap gap-3">
          <Btn variant="secondary" size="md" icon={<LogOut size={16} />} label="Sign out" onClick={signOut} testId="sign-out-button" />
          <Btn variant="ghost" size="md" label="Replay welcome tour" onClick={() => save.mutate({ tourDone: false })} testId="replay-tour-button" />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Btn variant="ghost" size="md" icon={<Trash2 size={16} />} label="Delete account" testId="delete-account-button" style={{ color: "var(--hot)" }} />
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This permanently erases your account and all entries, flips and cards.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} data-testid="delete-confirm" className="bg-red-500 hover:bg-red-600">Delete permanently</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Section>
    </Page>
  );
}
